import React, { useEffect, useRef, memo } from 'react';
import debounce from 'lodash/debounce';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '@store/hooks';
import { RootState } from '@store/store';
import { useChatPlayContext } from '@provider/ChatPlayProvider';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import useLocalStorage from '@hooks/useLocalStorage';
import { STORAGE_KEY } from '@common/values';
import RetryChatGPTPopup from '@components/commons/popup/RetryChatGPTPopup';

import ChatPopups from './ChatPopups';
import WaitItem from './chatItem/WaitItem';
import FirstPlayGuide from './chatItem/FirstPlayGuide';
import ChoiceInputImageItem from './chatItem/choice/ChoiceInputImageItem';
import ChoiceTimer from './chatItem/choice/ChoiceTimer';
import ChatBackground from './chatItem/ChatBackground';
import ChoiceInput from './chatItem/choice/ChoiceInput';
import { Choice, Statement } from '@/customTypes/chatStory';
import TopToastItemList from './chatItem/toast/TopToastItemList';
import ChatItem from './chatItem/ChatItem';
import ToastItem from './chatItem/toast/ToastList';
import ChapterEnding from './chatItem/ChapterEnding';
import SoundComponents from './SoundComponents';
import { isDevelopment } from '@/common/utils';

const ChatList = () => {
  const { playChapterInfo, isLoading } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );

  const {
    chatList,
    choice,
    endingPopupInfo,
    autoPlayType,
    scrollRef,
    storyItems,
    badgesAcquired,
    choiceStatistics,
    isShowRetryChatGPTPopup,
    currentStatement,
    fullScreenImagePopupInfo,
  } = useChatPlayContext();

  const {
    setAutoPlayType,
    getProfile,
    onChatProgress,
    onChoice,
    onSavePlay,
    onRemoteScriptChatGPT,
    setIsShowRetryChatGPTPopup,
  } = useChatPlayContext();

  /**
   * List State, Ref
   */
  const throttleRef = useRef<NodeJS.Timeout | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isClickableRef = useRef<boolean>(false);
  /**
   * List State, Ref
   */

  // 최초 플레이 여무 확인
  const [isPlay, setIsPlay] = useLocalStorage(STORAGE_KEY.IS_PLAY);
  // 최초 선택 여부 확인
  // const { localData: isChoice, setLocalData: setIsChoice } = useLocalStorage(
  //   STORAGE_KEY.IS_CHOICE,
  // );

  useEffect(() => {
    const onSpace = debounce(
      (e: KeyboardEvent) => {
        e?.code === 'Space' && onClickChat();
      },
      isDevelopment() ? 0 : 100,
    );

    window.addEventListener('keypress', onSpace);
    return () => {
      window.removeEventListener('keypress', onSpace);
    };
  }, [fullScreenImagePopupInfo]);

  useEffect(() => {
    isClickableRef.current =
      !endingPopupInfo?.isShow &&
      !choice &&
      autoPlayType !== 'play' &&
      !isLoading &&
      !storyItems &&
      !badgesAcquired &&
      !choiceStatistics;
  }, [
    choice,
    autoPlayType,
    endingPopupInfo,
    storyItems,
    badgesAcquired,
    choiceStatistics,
    isLoading,
  ]);

  useEffect(() => {
    if (process.browser) {
      window.onbeforeunload = () => {
        onSavePlay();
      }; // 새로고침 시 데이터 저장
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  useEffect(() => {
    return () => {
      onSavePlay();
    };
  }, []); // unmount 시 데이터 저장

  const onScrollEvent = () => {
    if (autoPlayType !== 'play' && scrollRef?.current && !throttleRef.current) {
      throttleRef.current = setTimeout(() => {
        const y =
          (scrollRef.current?.scrollHeight || 0) -
          (scrollRef.current?.clientHeight || 0) -
          (scrollRef.current?.scrollTop || 0);
        throttleRef.current = null;

        setAutoPlayType(y > 200 ? 'pause' : 'hidden');
      }, 500);
    }
  };

  const onClickChat = () => {
    if (fullScreenImagePopupInfo) {
      const popup = document.getElementById('full-screen-popup');
      popup?.click();
      return;
    }

    isClickableRef.current && onChatProgress('click');
  };

  const onClickRetryChatGPTPopup = () => {
    setIsShowRetryChatGPTPopup(false);
    onRemoteScriptChatGPT(currentStatement as Statement);
  };

  return (
    <div css={listContainer}>
      {isPlay && JSON.parse(isPlay) === false && (
        <div className="guide">
          <FirstPlayGuide
            onClose={() => {
              setIsPlay('true');
            }}
          />
        </div>
      )}
      <ChatPopups />
      <SoundComponents />
      <div
        className="list"
        ref={scrollRef}
        onClick={() => {
          onClickChat();
        }}
        onScroll={onScrollEvent}
      >
        {chatList?.map(
          (item, index) =>
            item?.isShow && (
              <ChatItem
                statement={item}
                key={`${item?.sourceLine}_${index}`}
                statements={chatList}
                characterProfile={getProfile(item)}
              />
            ),
        )}
        {choice &&
          choice.options?.layoutType === 'UserInputChoice' &&
          (choice.options?.choiceBackgroundImage ? (
            <ChoiceInputImageItem statement={choice} />
          ) : (
            <WaitItem />
          ))}
        {choice && choice.options?.layoutType !== 'UserInputChoice' && (
          <WaitItem />
        )}
        <ChapterEnding />
        <div ref={bottomRef}></div>
      </div>
      <TopToastItemList />
      <ChoiceTimer choice={choice} onChoice={onChoice} />
      <ChatBackground isClickableRef={isClickableRef} />
      <ToastItem
        playChapterInfo={playChapterInfo}
        onPress={() => {
          onClickChat();
        }}
      />
      <RetryChatGPTPopup
        isShow={isShowRetryChatGPTPopup}
        onClose={onClickRetryChatGPTPopup}
      />
      <ChoiceInput choice={choice as Choice} />
    </div>
  );
};

const listContainer = css`
  position: relative;
  height: 100%;
  overflow-y: hidden;
  background-color: ${theme.colors.gray200};

  .guide {
    width: 100%;
    max-width: 50em;
    height: 100%;
    position: fixed;
    z-index: 20;
  }

  .list {
    height: 70%;
    overflow-y: scroll;
    padding: 20px 0;
    cursor: pointer;

    /* 수평 스크롤 바 */
    ::-webkit-scrollbar {
      width: 0;
    }
  }
`;

export default memo(ChatList);
